/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {FILTER_DRAWER_PAPER_ID} from '@ion/dls/src/components/drawer/FilterDrawer';
import {useAppliedFilterBarStyles} from '@ion/dls/src/filters/AppliedFilterBar.styles';
import AppliedFilterClearAllConfirmation from '@ion/dls/src/filters/AppliedFilterClearAllConfirmation';
import FilterFavoriteDeleteDialog from '@ion/dls/src/filters/FilterFavoriteDeleteDialog';
import FilterFavoriteDialog from '@ion/dls/src/filters/FilterFavoriteDialog';
import {TrackingFilterIcon} from '@ion/dls/src/icons/current/TrackingFilterIcon';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import Render from '@ion/dls/src/render/Render';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {IconSize} from '@ion/dls/src/types/types';
import {FavoriteDialogLabels, FavoriteDialogState, FavoriteDialogType} from './favoriteDialogTypes';
import {TrackingFavoriteNameValidationError} from './favoriteUtils';

const FAVORITE_SNACKBAR_Z_INDEX = 2147483647;

// these dialogs belong to the filter drawer, so they must render above it -
// FilterDrawer.tsx now sits at trackingFilterSelectedFiltersConfirmation+1,
// so this needs to clear that too rather than the previous hardcoded
// 100001/100003 (which the raised drawer would now sit above) ...
const getFilterDrawerDialogZIndex = () => dlsZIndex('trackingFilterSelectedFiltersConfirmation') + 2;

// the drawer's panel, which its confirmations centre over ...
const drawerPaperRef = {
  get current() {
    return document.getElementById(FILTER_DRAWER_PAPER_ID);
  }
};

export interface TrackingFilterDrawerDialogsProps {
  actionsClassName: string;
  applyDisabled?: boolean;
  deleteFavoriteName?: string | null;
  favoriteDialog?: FavoriteDialogState | null;
  favoriteDialogLabels: FavoriteDialogLabels;
  favoriteSnackbarMessage?: string | null;
  rootClassName: string;
  showClearAllConfirmation: boolean;
  showLeaveConfirmation: boolean;
  showUnappliedChanges: boolean;
  getFavoriteErrorText: (error?: TrackingFavoriteNameValidationError | null) => string | undefined;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onCloseFavoriteDialog: () => void;
  onCloseFavoriteSnackbar: () => void;
  onDeleteFavorite: () => void;
  onDismissClearAllConfirmation: () => void;
  onDismissUnappliedChanges: () => void;
  onDiscardChanges: () => void;
  onFavoriteDialogPrimaryAction: (name: string) => void;
  onFavoriteDialogSecondaryAction: () => void;
  onLeaveTracking: () => void;
  onRequestFavoriteDeleteCancel: () => void;
  onStayOnTracking: () => void;
}

/**
 * Renders all modal and snackbar surfaces used by the tracking filter drawer.
 * Its confirmations - Clear All, Unapplied Changes and Leave Tracking - are
 * the applied filter bar's own Clear All confirmation, centred over the
 * drawer's panel.
 *
 * @param props Defines dialog state, labels, styling, and action callbacks.
 * @returns Tracking filter drawer dialogs and snackbar.
 */
export const TrackingFilterDrawerDialogs = ({
  actionsClassName,
  deleteFavoriteName,
  favoriteDialog,
  favoriteDialogLabels,
  favoriteSnackbarMessage,
  rootClassName,
  showClearAllConfirmation,
  showLeaveConfirmation,
  showUnappliedChanges,
  getFavoriteErrorText,
  onApplyFilters,
  onClearFilters,
  onCloseFavoriteDialog,
  onCloseFavoriteSnackbar,
  onDeleteFavorite,
  onDismissClearAllConfirmation,
  onDismissUnappliedChanges,
  onDiscardChanges,
  onFavoriteDialogPrimaryAction,
  onFavoriteDialogSecondaryAction,
  onLeaveTracking,
  onRequestFavoriteDeleteCancel,
  onStayOnTracking,
  applyDisabled = false
}: TrackingFilterDrawerDialogsProps) => {
  const confirmationClasses = useAppliedFilterBarStyles();
  // re-renders on a light/dark switch, so the Apply icon color follows the mode ...
  useThemeMode();

  return (
    <>
      <Render.If condition={showClearAllConfirmation}>
        <AppliedFilterClearAllConfirmation centered
          classes={confirmationClasses}
          anchorRef={drawerPaperRef}
          clearAllLabel={translate('filters:drawer.clearAll')}
          message={translate('filters:drawer.clearAllConfirmation.message')}
          secondaryLabel={translate('filters:drawer.clearAllConfirmation.keep')}
          title={translate('filters:drawer.clearAllConfirmation.title')}
          zIndex={getFilterDrawerDialogZIndex()}
          onCancel={onDismissClearAllConfirmation}
          onConfirm={onClearFilters} />
      </Render.If>
      <Render.If condition={showUnappliedChanges}>
        <AppliedFilterClearAllConfirmation centered
          classes={confirmationClasses}
          anchorRef={drawerPaperRef}
          clearAllLabel={translate('filters:drawer.apply')}
          message={translate('filters:drawer.unappliedChanges.message')}
          primaryDisabled={applyDisabled}
          primaryIcon={<TrackingFilterIcon color={dlsColor('white')} />}
          secondaryLabel={translate('filters:drawer.unappliedChanges.discard')}
          title={translate('filters:drawer.unappliedChanges.title')}
          zIndex={getFilterDrawerDialogZIndex()}
          onCancel={onDismissUnappliedChanges}
          onConfirm={onApplyFilters}
          onSecondary={onDiscardChanges} />
      </Render.If>
      <Render.If condition={showLeaveConfirmation}>
        <AppliedFilterClearAllConfirmation centered
          classes={confirmationClasses}
          anchorRef={drawerPaperRef}
          clearAllLabel={translate('filters:tracking.leaveConfirmation.leave')}
          message={translate('filters:tracking.leaveConfirmation.message')}
          primaryIcon={null}
          secondaryLabel={translate('filters:tracking.leaveConfirmation.stay')}
          title={translate('filters:tracking.leaveConfirmation.title')}
          zIndex={getFilterDrawerDialogZIndex()}
          onCancel={onStayOnTracking}
          onConfirm={onLeaveTracking} />
      </Render.If>
      <Render.If condition={!!favoriteDialog}>
        <FilterFavoriteDialog open={!!favoriteDialog}
          title={favoriteDialogLabels.title}
          description={favoriteDialogLabels.description}
          inputLabel={favoriteDialogLabels.inputLabel}
          placeholder={favoriteDialogLabels.placeholder}
          initialName={favoriteDialog?.name ?? ''}
          cancelLabel={favoriteDialogLabels.cancel}
          primaryLabel={favoriteDialogLabels.primaryLabel}
          secondaryLabel={favoriteDialog?.type === FavoriteDialogType.Edit
            ? translate('filters:tracking.favorites.dialog.saveAsNew')
            : undefined}
          errorText={getFavoriteErrorText(favoriteDialog?.error)}
          className={rootClassName}
          zIndex={getFilterDrawerDialogZIndex()}
          onCancel={onCloseFavoriteDialog}
          onPrimaryAction={onFavoriteDialogPrimaryAction}
          onSecondaryAction={favoriteDialog?.type === FavoriteDialogType.Edit
            ? onFavoriteDialogSecondaryAction
            : undefined} />
      </Render.If>
      <Render.If condition={!!deleteFavoriteName}>
        <FilterFavoriteDeleteDialog open={!!deleteFavoriteName}
          cancelLabel={translate('filters:tracking.favorites.delete.cancel')}
          className={rootClassName}
          message={translate('filters:tracking.favorites.delete.message')}
          title={translate('filters:tracking.favorites.delete.title')}
          primaryLabel={translate('filters:tracking.favorites.delete.action')}
          zIndex={getFilterDrawerDialogZIndex()}
          onCancel={onRequestFavoriteDeleteCancel}
          onDelete={onDeleteFavorite} />
      </Render.If>
      <IonSnackbar show={!!favoriteSnackbarMessage}
        message={favoriteSnackbarMessage}
        actionLabel={translate('filters:tracking.favorites.snackbar.okay')}
        zIndex={FAVORITE_SNACKBAR_Z_INDEX}
        autoHideDuration={3000}
        onAction={onCloseFavoriteSnackbar}
        onClose={onCloseFavoriteSnackbar} />
    </>
  );
};

export default memo(TrackingFilterDrawerDialogs);
